import { Stack, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { type Control, useWatch } from "react-hook-form";
import { MonthlyProjectionRow } from "web_myplann/Goals/Components/AmountSections/MonthlyProjectionRow";
import { RemainingRow } from "web_myplann/Goals/Components/AmountSections/RemainingRow";
import { RentCalculator } from "web_myplann/Goals/Components/AmountSections/RentCalculator";
import type { GoalFormModel } from "web_myplann/Goals/Forms/useGoalForm";
import { useGoalCategoryStrings } from "web_myplann/Goals/Hooks/useGoalCategoryStrings";
import type { GoalCategory } from "web_myplann/Goals/Types/GoalTypes";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowHint } from "web_myplann/Shared/Components/FlatRow/FlatRowHint";

type Props = {
	control: Control<GoalFormModel>;
	category: GoalCategory | undefined;
};

export const WithLoanAmountSection = ({ control, category }: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const {
		amountLabel,
		ownFundsLabel,
		loanLabel,
		loanHint,
		remainingLabel,
		rentCalcAvailable,
		rentCalcDefaultOn,
	} = useGoalCategoryStrings(category);

	const [useRentCalc, setUseRentCalc] = useState(rentCalcDefaultOn);

	useEffect(() => {
		setUseRentCalc(rentCalcAvailable ? rentCalcDefaultOn : false);
	}, [rentCalcAvailable, rentCalcDefaultOn]);

	const totalValue = useWatch({ control, name: "totalValue" }) ?? 0;
	const ownFunds = useWatch({ control, name: "ownFunds" }) ?? 0;
	const loanAmount = useWatch({ control, name: "loanAmount" }) ?? 0;
	const dueDate = useWatch({ control, name: "dueDate" });

	const remaining = Math.max(0, totalValue - ownFunds - loanAmount);
	const showExtras = totalValue > 0;
	const currencySuffix = t(Resources.Goals.Amount.currencySign);

	return (
		<Stack bgcolor={theme.palette.background.paper}>
			{rentCalcAvailable && (
				<RentCalculator
					control={control}
					useRentCalc={useRentCalc}
					onToggle={setUseRentCalc}
				/>
			)}
			{!useRentCalc && (
				<FlatRowFormInput
					control={control}
					name="totalValue"
					label={amountLabel}
					placeholder={t(Resources.Goals.Form.amountPlaceholder)}
					type="number"
					suffix={currencySuffix}
					isLast={!showExtras}
				/>
			)}
			{showExtras && (
				<>
					<FlatRowFormInput
						control={control}
						name="ownFunds"
						label={ownFundsLabel}
						placeholder="0"
						type="number"
						suffix={currencySuffix}
					/>
					<FlatRowFormInput
						control={control}
						name="loanAmount"
						label={loanLabel}
						placeholder="0"
						type="number"
						suffix={currencySuffix}
					/>
					{loanHint && <FlatRowHint text={loanHint} />}
					<RemainingRow label={remainingLabel} amount={remaining} />
					<MonthlyProjectionRow
						savingsTarget={remaining}
						dueDate={dueDate}
						compact
					/>
				</>
			)}
		</Stack>
	);
};
